import { useMutation, useQuery } from "@tanstack/react-query";
import { NotificationModel, PagedNotificationModel } from "./types";
import useNotificationsStore from "./store";
import useMainStore from "../../landing-page/api/store";
import { useDelete, useGet, usePut } from "../../../common/api-service";
import queryClient from "../../../common/query-client";
import { useGetMyProfile } from "../../landing-page/api/hooks";

export const useGetMyNotifications = () => {
  const { get } = useGet();
  const mainStore = useMainStore();
  const url = `/api/notification/my-notifications`;

  return useQuery({
    queryFn: () => get<NotificationModel[]>(url),
    queryKey: [
      "my-notifications",
      {
        "current-user": mainStore.currentUser?.id,
      },
    ],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetPagedNotifications = () => {
  const { get } = useGet();
  const { initialNotificationState } = useNotificationsStore();
  var url = `/api/notification/paged-notifications?PageNumber=${initialNotificationState.pageNumber}&PageSize=${initialNotificationState.pageSize}&Filter=${initialNotificationState.filter}`;
  if (initialNotificationState.searchText) {
    url = url + `&SearchText=${initialNotificationState.searchText}`;
  }
  return useQuery<PagedNotificationModel>({
    queryFn: () => get<PagedNotificationModel>(url),
    queryKey: [
      "paged-notifications",
      {
        PageNumber: initialNotificationState.pageNumber,
        PageSize: initialNotificationState.pageSize,
        Filter: initialNotificationState.filter,
        SearchText: initialNotificationState.searchText,
      },
    ],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useGetMyLatestRemovedFromFacilityNotification = () => {
  const { get } = useGet();
  const notificationsStore = useNotificationsStore();
  const mainStore = useMainStore();
  const url = `/api/notification/my-latest-removed-from-facility-notification`;

  return useQuery({
    queryFn: () => get<NotificationModel>(url),
    queryKey: [
      "latest-removed-from-facility-notification",
      {
        "current-user": mainStore.currentUser?.id,
      },
    ],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
    enabled: notificationsStore.showNoFacilityModal !== false,
  });
};

export const useArchiveNotification = () => {
  const { put } = usePut();
  const {
    initialNotificationState: { pageNumber, pageSize, filter, searchText },
    selectedNotifId,
    setSelectedNotifId,
  } = useNotificationsStore();

  return useMutation<string, Error, void>({
    mutationFn: async (): Promise<string> => {
      const payload = { Id: selectedNotifId };

      const response = await put<typeof payload, string>(
        "/api/notification/archive",
        payload,
        true
      );
      return response as string;
    },
    onSuccess: () => {
      setSelectedNotifId("");
      queryClient.invalidateQueries({
        queryKey: [
          "paged-notifications",
          {
            PageNumber: pageNumber,
            PageSize: pageSize,
            Filter: filter,
            SearchText: searchText,
          },
        ],
        exact: true,
      });
    },
  });
};

export const useIsReadNotification = () => {
  const { put } = usePut();
  const {
    initialNotificationState: { pageNumber, pageSize, filter, searchText },
    selectedNotifId,
    setSelectedNotifId,
  } = useNotificationsStore();
  const mainStore = useMainStore();
  const getMyProfile = useGetMyProfile();

  return useMutation<string, Error, void>({
    mutationFn: async (): Promise<string> => {
      const payload = { Id: selectedNotifId };

      const response = await put<typeof payload, string>(
        "/api/notification/read",
        payload,
        true
      );
      return response as string;
    },
    onSuccess: () => {
      setSelectedNotifId("");
      queryClient.invalidateQueries({
        queryKey: [
          "paged-notifications",
          {
            PageNumber: pageNumber,
            PageSize: pageSize,
            Filter: filter,
            SearchText: searchText,
          },
        ],
        exact: true,
      });
      queryClient.invalidateQueries({ queryKey: ["my-notifications", {
            "current-user": mainStore.currentUser?.id,
        }],});
      getMyProfile.refetch();
    },
  });
};

export const useDeleteNotification = () => {
  const { deleteRequest } = useDelete();
  const {
    initialNotificationState: { pageNumber, pageSize, filter, searchText },
  } = useNotificationsStore();
  const mainStore = useMainStore();
  const getMyProfile = useGetMyProfile();

  return useMutation({
    mutationFn: async (id: string) => {
      const url = `/api/notification/delete/${id}`;
      return deleteRequest(url);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "paged-notifications",
          {
            PageNumber: pageNumber,
            PageSize: pageSize,
            Filter: filter,
            SearchText: searchText,
          },
        ],
        exact: true,
      });
      queryClient.invalidateQueries({ queryKey: ["my-notifications", {
            "current-user": mainStore.currentUser?.id,
        }],});
      getMyProfile.refetch();
    },
  });
};

export interface MarkAllReadStatusProps {
  ids: string[];
  isRead: boolean;
}

export const useMarkAllReadStatus = () => {
  const { put } = usePut();
  const {
    initialNotificationState: { pageNumber, pageSize, filter, searchText },
  } = useNotificationsStore();
  const mainStore = useMainStore();
  const getMyProfile = useGetMyProfile();

  return useMutation<string, Error, MarkAllReadStatusProps>({
    mutationFn: async ({ ids, isRead }): Promise<string> => {
      const payload = { Ids: ids, IsRead: isRead };
      const response = await put<typeof payload, string>(
        "/api/notification/update-all-read",
        payload,
        true
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "paged-notifications",
          {
            PageNumber: pageNumber,
            PageSize: pageSize,
            Filter: filter,
            SearchText: searchText,
          },
        ],
        exact: true,
      });
      queryClient.invalidateQueries({ queryKey: ["my-notifications", {
            "current-user": mainStore.currentUser?.id,
        }],});
      getMyProfile.refetch();
    },
  });
};
